import { Button, StatusPill } from '../../../design-system';
import './AgentMachinePanel.css';

// What one registered machine is actually doing, and what to do about it.
//
// ⚠️ EVERY VERDICT HERE COMES FROM THE SERVER, and that is the point. The
// browser has no way to know whether a PC in another office is switched on, so
// `utils/agentHealth.js` derives it from the machine's own heartbeat and this
// component renders the answer. Recomputing any of it here — "if lastSeenAt is
// recent…" — would be a second definition of working, and the two would part
// company the first time either was touched.
//
// ⚠️ IT WARNS, IT DOES NOT BLOCK. Same rule as the attendance geofence: the
// checks classify, they never permit. A machine that reports its Tally gateway
// down at 9:58 may have had Tally opened at 9:59, and refusing to let the user
// press the button would make this panel's worst guess final. So the reason is
// stated, the fix is offered, and the action stays available.

const LIVENESS = {
  online: { variant: 'success', label: 'Running now' },
  stale: { variant: 'warning', label: 'Not running' },
  offline: { variant: 'danger', label: 'Not running' },
  never: { variant: 'danger', label: 'Never started' },
};

function whenLastSeen(iso) {
  if (!iso) return 'has never checked in';
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 2) return 'checked in just now';
  if (mins < 60) return `checked in ${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `checked in ${hours}h ago`;
  const days = Math.round(hours / 24);
  return `checked in ${days} day${days === 1 ? '' : 's'} ago`;
}

function AgentMachinePanel({ agent, busy = false, onReinstall, onForget }) {
  if (!agent) return null;

  const live = LIVENESS[agent.liveness] || LIVENESS.never;
  const problems = agent.selfCheck?.problems || [];
  const outdated = agent.versionState === 'outdated';

  return (
    <div className="agentm">
      <div className="agentm-head">
        <div>
          <div className="agentm-name">{agent.label}</div>
          <div className="agentm-sub">
            {agent.platform || 'unknown platform'} · {agent.version || 'unknown version'} ·{' '}
            {whenLastSeen(agent.lastSeenAt)}
          </div>
        </div>
        <div className="agentm-pills">
          <StatusPill variant={live.variant} label={live.label} />
          {/* Only ever shown when we know both sides. `unknown` means the server
              has no published build to compare against, and inventing a verdict
              there would report every machine as current. */}
          {outdated && <StatusPill variant="warning" label="Update available" />}
        </div>
      </div>

      {!agent.tally?.ready && (
        <p className="agentm-warn">
          {agent.tally?.message || 'This computer cannot sync Tally yet.'}
        </p>
      )}

      {/* The machine's own report. It is the only view of the things the server
          cannot see — a broken launcher, a missing install, a gateway that is
          switched off — so it is shown verbatim rather than summarised into
          "something is wrong". */}
      {problems.length > 0 && (
        <ul className="agentm-problems">
          {problems.map((p) => (
            <li key={p.name} className={p.state === 'bad' ? 'is-bad' : 'is-warn'}>
              <strong>{p.name}:</strong> {p.detail}
              {p.fix && <span className="agentm-fix"> — {p.fix}</span>}
            </li>
          ))}
        </ul>
      )}

      {agent.selfCheck?.state === 'expired' && (
        <p className="agentm-muted">
          Its last self-check is over a day old, so this may not be its current state.
        </p>
      )}

      <div className="agentm-actions">
        {onReinstall && (
          <Button variant="secondary" onClick={() => onReinstall(agent)} disabled={busy}>
            {outdated ? 'Update this computer' : 'Reinstall'}
          </Button>
        )}
        {onForget && (
          <Button variant="ghost" onClick={() => onForget(agent)} disabled={busy}>
            Forget this computer
          </Button>
        )}
      </div>
    </div>
  );
}

export default AgentMachinePanel;
